import { Store } from '@ngxs/store';
import { combineLatest, defer, Observable, of } from 'rxjs';
import { map, scan, shareReplay, switchMap, take } from 'rxjs/operators';

import { distinctUntilObjectChanged, ignoreNullish, isEmpty, Loadable, Logger, removeDuplicates } from '@aiwyn/shared';
import {
  BaseClient,
  BaseServiceLine,
  ClientRequestGroupJobModel,
  ClientRequestGroupModel,
  ClientRequestGroupType,
  JobStatus,
  StaffEntity,
} from '@aiwyn/api';

import { ClientRequestIdentityHelper, DeliverableIdentityType, RequestIdentityType } from '../utility';
import { ClientRequestGroupStatus, TypedIdentity } from '../models';
import { CommonClientRequestsState } from '../state';
import { AbstractBaseCommonClientRequestController } from './abstract-base-common-client-request-controller.model';
import { BaseCommonClientRequest } from './common-client-request';
import { BaseCommonClientRequestAssignee } from './common-client-request-assignee';
import { BaseCommonClientRequestClient } from './common-client-request-client';
import { BaseCommonDeliverable } from './common-deliverable';

export class BaseCommonClientRequestGroup<
  ClientRequestGroup extends ClientRequestGroupModel = ClientRequestGroupModel,
  ClientRequestGroupTypeClass extends CommonGroupModelToTypeClassMap<ClientRequestGroup> = CommonGroupModelToTypeClassMap<ClientRequestGroup>,
> extends AbstractBaseCommonClientRequestController<TypedIdentity<ClientRequestGroupType>, ClientRequestGroup, ClientRequestGroupTypeClass> {
  model$: Observable<Loadable<ClientRequestGroup>> = defer(() =>
    this._store.select(CommonClientRequestsState.getClientRequestGroupLoadable(this.identity)) as Observable<Loadable<ClientRequestGroup>>,
  ).pipe(
    ignoreNullish(),
    shareReplay(1),
  );

  protected _create$(
    model$: Observable<Loadable<ClientRequestGroup>>,
    store: Store,
    logger: Logger,
  ): Observable<ClientRequestGroupTypeClass> {
    return model$.pipe(
      map(model => model.data?.type),
      ignoreNullish(),
      take(1),
      map(type => {
        return new CommonClientRequestGroupJob(
          model$ as Observable<Loadable<ClientRequestGroupJobModel>>,
          store,
          logger,
        ) as ClientRequestGroupTypeClass;
      }),
    );
  }

  protected readonly requestIdentities$: Observable<TypedIdentity<RequestIdentityType>[]> = this.model$.pipe(
    switchMap(model => this._store.select(CommonClientRequestsState.getClientRequestLoadablesInGroup(model))),
    map(requests => requests.map(request => request.identity as TypedIdentity<RequestIdentityType>)),
    distinctUntilObjectChanged(),
    shareReplay(1),
  );

  requests$: Observable<BaseCommonClientRequest[]> = this.requestIdentities$.pipe(
    scan(
      (existingRequests, requestIdentities) => {
        const newRequestIdentities = requestIdentities.filter(requestId =>
          !existingRequests.some(({ identity }) => identity === requestId),
        );

        return existingRequests.concat(newRequestIdentities.map(requestId =>
          new BaseCommonClientRequest(requestId, this._store, this._logger),
        ));
      },
      [] as BaseCommonClientRequest[],
    ),
    shareReplay(1),
  );

  isLocked$: Observable<boolean> = this.requests$.pipe(
    switchMap(requests => {
      if (isEmpty(requests)) return of(false);
      return combineLatest(requests.map(r => r.isLocked$)).pipe(
        map(lockStates => lockStates.some(locked => locked)),
      );
    }),
    shareReplay(1),
  );

  deliverables$: Observable<BaseCommonDeliverable[]> = this.model$.pipe(
    switchMap(model => this._store.select(CommonClientRequestsState.getDeliverablesInGroup(model))),
    scan(
      (existingDeliverables, deliverables) => {
        const newDeliverables = deliverables.filter(deliverable =>
          !existingDeliverables.some(({ identity }) => identity === deliverable.identity),
        );

        return existingDeliverables.concat(newDeliverables.map(deliverable =>
          new BaseCommonDeliverable(deliverable.identity as TypedIdentity<DeliverableIdentityType>, this._store, this._logger),
        ));
      },
      [] as BaseCommonDeliverable[],
    ),
    shareReplay(1),
  );

  earliestDueDate$: Observable<string | undefined> = this.requests$.pipe(
    // Needed to look specifically if there are no Requests, because if it was empty, it wasn't raising through CombinedLatest properly.
    // In older versions of RxJS (before v6.5.0), combineLatest([]) would not emit anything (it would hang)
    map(requests => {
      // First check if requests array is empty
      if (requests.length === 0) {
        return undefined;
      }

      // If we have requests, continue with combineLatest logic
      return combineLatest(
        requests.map(request => request.nextDueDate$)
      ).pipe(
        map(dueDates => {
          // Filter out undefined/null values
          const validDates = dueDates.filter(date => !!date);
          // If no valid dates exist, return undefined
          if (validDates.length === 0) {
            return undefined;
          }
          // Convert strings to Date objects and find the minimum
          const dateObjects = validDates.map(dateStr => new Date(dateStr as string));
          const earliestDate = new Date(Math.min(...dateObjects.map(date => date.getTime())));
          // Return the earliest date in the original string format
          return validDates.find(dateStr =>
            new Date(dateStr as string).getTime() === earliestDate.getTime());
        })
      );
    }),
    // Use switchMap to flatten the inner Observable
    switchMap(result => {
      // If the result is undefined (empty requests array), emit undefined
      if (result === undefined) {
        return of(undefined);
      }
      // Otherwise return the inner Observable
      return result;
    }),
    shareReplay(1),
  );

  dueDate$: Observable<string | undefined> = this.model$.pipe(
    map(a => a.data.job.endDt),
    shareReplay(1),
  );

  assignees$: Observable<BaseCommonClientRequestAssignee[]> = this.requests$.pipe(
    switchMap(requestClasses => combineLatest(requestClasses.map(requestClass => requestClass.assignees$))),
    map(assignees => assignees.reduce(
      (acc, assignees) => acc.concat(assignees.filter(assignee => !acc.some(({ identity }) => identity === assignee.identity))),
      [] as BaseCommonClientRequestAssignee[],
    )),
    shareReplay(1),
  );

  // this allows for ungrouped requests from different clients to be shown together
  clients$: Observable<BaseCommonClientRequestClient[]> = this.requests$.pipe(
    switchMap(requestClasses => combineLatest(requestClasses.map(requestClass => requestClass.client$))),
    map(clients => {
      const allClientIdentities = clients.map(({ identity }) => identity);
      const uniqueClientIdentities = removeDuplicates(allClientIdentities);
      return uniqueClientIdentities.map(clientIdentity => clients.find(({ identity }) => identity === clientIdentity));
    }),
    shareReplay(1),
  );

  staff$: Observable<Loadable<StaffEntity>[]> = this.requests$.pipe(
    switchMap(requests => combineLatest(requests.map(request => request.staff$))),
    map(staffs => staffs.reduce(
      (acc, staff) => acc.some(({ identity }) => identity === staff.identity) ? acc : acc.concat(staff),
      [] as Loadable<StaffEntity>[],
    )),
    shareReplay(1),
  );
}

export abstract class AbstractCommonClientRequestGroup<
  ClientRequestGroup extends ClientRequestGroupModel = ClientRequestGroupModel,
> {
  constructor(
    protected readonly model$: Observable<Loadable<ClientRequestGroup>>,
    protected readonly _store: Store,
    protected readonly _logger: Logger,
  ) {}

  abstract name$: Observable<string>;
  abstract status$: Observable<ClientRequestGroupStatus>;
}

export class CommonClientRequestGroupJob extends AbstractCommonClientRequestGroup<ClientRequestGroupJobModel> {
  name$ = this.model$.pipe(
    map(({data}) => data.job.name || data.job.serviceDescription),
    shareReplay(1),
  );

  client$ = this.model$.pipe(
    map(({ data }) => ClientRequestIdentityHelper.getClientIdentity({ id: data.job.clientId } as BaseClient)),
    map(client => new BaseCommonClientRequestClient(client, this._store, this._logger)),
    shareReplay(1),
  );

  status$ = this.model$.pipe(
    map(({ data }) => [JobStatus.OPEN, JobStatus.DRAFT].includes(data.job.status) ? ClientRequestGroupStatus.OPEN : ClientRequestGroupStatus.COMPLETED),
    shareReplay(1),
  );

  serviceLines$: Observable<BaseServiceLine[]> = this.model$.pipe(
    switchMap(({ data }) => this._store.select(CommonClientRequestsState.getServiceLinesLoadablesByJobId(data.job.id))),
    map(serviceLines => serviceLines.map(serviceLine => serviceLine.data).filter(Boolean)),
    shareReplay(1),
  );
}

export type CommonGroupModelToTypeClassMap<ClientRequestGroup extends ClientRequestGroupModel> =
  ClientRequestGroup extends ClientRequestGroupJobModel
    ? CommonClientRequestGroupJob
    : never;
